import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { Ptag } from "components/core/fonts";
import ECard from "./ECard";
import { ECardHeader, ECardLink, ECardScroll, Line } from "./styles";
import { PDFDownloadLink } from "@react-pdf/renderer";
import ECardPdf from "components/ECardPdf";
import { FLEX_PRODUCTS } from "pages/FlexSetup/constants";
import http from "services/httpService";


const ECardList = ({ abbr, members }) => {

  const sortedMembers = members.sort((a, b) => {
    if (a.relationship === "SELF") return -1; // Move "SELF" to the first
    if (b.relationship === "SELF") return 1;  // Move others down
    return 0; // No change for other relationships
  });
  const navigate = useNavigate();
  // getting title based on the abbreviation
  const title = useMemo(() => {
    let title = "";
    FLEX_PRODUCTS.find((product) => {
      if (product.value === abbr) {
        title = product.display_name;
        return title;
      }
    });
    switch (abbr) {
      case "GMC":
      case "GMC_M":
        title = "Group Mediclaim Cover";
        break;
      case "GPA":
        title = "Group Personal Accident";
        break;
      case "GTL":
        title = "Group Term Life";
        break;
    }
    return title;
  }, [abbr]);

  const getProductCommercialAbbr = () => {
    switch (abbr) {
      case "GMC":
      case "GMC_M":
        return "GHI";
      default:
        return abbr;
    }
  };

  const ecardDownloadHandler = async (vitalCoverId) => {
    try {
      const { data } = await http.post(
        `business/download_e_card/?vital_cover_id=${vitalCoverId}`,
      );
      return data;
    } catch (error) {
      console.log("# E card Download failure:", error);
      return false;
    }
  };

  const ecardDownloadAsPDF = async (vitalCoverId) => {
    try {
      const response = await http.post(
        `business/download_e_card/?vital_cover_id=${vitalCoverId}`,
        {}, // Empty body if required by your API
        { responseType: "blob" }, // CRITICAL: Tells axios to treat response as a file/blob
      );

      // check the response type (MIME type)
      console.log("Content-Type:", response.headers["content-type"]);

      // check if the data is an instance of Blob
      console.log("Is Blob:", response.data instanceof Blob);

      return response.data;
    } catch (error) {
      console.error("# E card Download failure:", error);
      return false;
    }
  };

  const handleECardDownload = async (event) => {
    let getSingleMemberVitalCoverId = members[0].vital_cover_id;

    const eCardResponse = await ecardDownloadHandler(
      getSingleMemberVitalCoverId,
    );

    if (eCardResponse && eCardResponse.e_card_download) {
      event.preventDefault();

      try {

        // 1. Fetch the file data
        const response = await fetch(eCardResponse.e_card_download);
        const blob = await response.blob();

        // 2. Create a local URL for the blob object
        const url = window.URL.createObjectURL(blob);

        // 3. Create the hidden link
        const link = document.createElement("a");
        link.href = url;

        // Now the 'download' attribute will be respected
        const fileName = `E_Cards.pdf`;
        link.setAttribute("download", fileName);

        document.body.appendChild(link);
        link.click();

        // 4. Cleanup
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        console.error("Download failed, falling back to window.open", err);
        // Fallback: Just open the link if fetch fails (e.g., CORS issues)
        window.open(eCardResponse.e_card_download, "_blank");
      }
    }
  };

  const handleECardDownloadBase64 = async (event, fileName) => {
    let getSingleMemberVitalCoverId = members[0].vital_cover_id;
    const data = await ecardDownloadAsPDF(getSingleMemberVitalCoverId);

    // 1. Ensure we have a Blob.
    // If 'data' is already a Blob from fetch/axios, use it directly.
    // If 'data' is a base64 string, convert it first.
    const blob =
      data instanceof Blob
        ? data
        : new Blob([data], { type: "application/pdf" });

    const link = document.createElement("a");
    const url = window.URL.createObjectURL(blob);

    link.href = url;
    link.download = fileName.endsWith(".pdf") ? fileName : `${fileName}.pdf`;

    // Append, trigger, and cleanup
    document.body.appendChild(link);
    link.click();

    // Use a slight timeout for cleanup to ensure the browser triggers the download
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);
  };

  const renderECardDownload = () => {
    // Removed async
    // No await allowed here
    let eCardType = members.length > 0 ? members[0]["e_card_type"] : null;
    let linkStyle = {
      fontFamily: "Inter",
      color: "rgb(0, 84, 227)",
      fontSize: "14px",
      fontWeight: "600",
      textDecoration: "underline",
      cursor: "pointer",
    };

    if (eCardType === "LINK") {
      return (
        <div style={linkStyle} onClick={handleECardDownload}>
          Download E-Cards
        </div>
      );
    }

    if (eCardType === "PDF") {
      let fileName=`${getProductCommercialAbbr()}_E_Cards.pdf`
      return (
        <div style={linkStyle} onClick={(e) => handleECardDownloadBase64(e, fileName)}>
          Download E-Cards
        </div>
      );
    }

    if (eCardType === "CUSTOM") {
      return (
        <PDFDownloadLink
          document={<ECardPdf members={members} />}
          fileName={`${getProductCommercialAbbr()}_E_Cards.pdf`}
        >
          <ECardLink>Download E-Cards</ECardLink>
        </PDFDownloadLink>
      );
    }

    // Always return something (or null) to avoid render errors
    return;
  };

  return (
    <ECardScroll>
      <ECardHeader>
        <Ptag marginLeft="1rem" fontSize="20px" fontWeight="600">
          {title}
        </Ptag>
        {renderECardDownload()}
        {/* <PDFDownloadLink
          document={<ECardPdf members={members} />}
          fileName={`${getProductCommercialAbbr()}_E_Cards.pdf`}
        >
          <ECardLink>Download E-Cards</ECardLink>
        </PDFDownloadLink> */}
      </ECardHeader>
      <Line />
      <div
        style={{
          marginTop: "2.2rem",
          display: "flex",
          overflow: "auto",
          gap: "2.4rem",
          padding: "2rem 1rem",
        }}
      >
        {sortedMembers.map((member, index) => (
          <ECard key={index.toString()} member={member} />
        ))}
      </div>
    </ECardScroll>
  );
};

export default ECardList;
